/**
 * Inputs: selected booking/household. Outputs: supplier payment or reimbursement POST.
 * Dirty fields stay put when the trip refreshes in the background.
 */

import { useEffect, useRef, useState } from "react";
import { defaultFundedAllocations, fundingPreviewText, remainingHouseholdAmounts } from "@shared/funding.ts";
import { previewPayment } from "@shared/ledger.ts";
import { formatMinor } from "@shared/money.ts";
import { bookingDisplayTitle } from "@shared/upcoming.ts";
import { createId } from "@shared/ids.ts";
import { todayInZone } from "@shared/time.ts";
import { refundableRemainder } from "@shared/financial.ts";
import { useTrip } from "../../app/trip.tsx";
import { postPayment, postRefund, postSettlement } from "../../lib/api.ts";
import type { Allocation, TripDocument } from "@shared/schema.ts";

function remainingTotal(trip: TripDocument, bookingId: string): number {
  const booking = trip.bookings.find((b) => b.id === bookingId);
  if (!booking) return 0;
  const remaining = remainingHouseholdAmounts(
    booking,
    trip.supplierPayments,
    trip.supplierRefunds,
    trip.households.map((h) => h.id),
  );
  return Object.values(remaining).reduce((sum, n) => sum + n, 0);
}

export function PaymentForm({
  onSaved,
  initialBookingId,
  initialPayer,
}: {
  onSaved: () => void;
  initialBookingId?: string;
  initialPayer?: string;
}) {
  const { trip } = useTrip();
  const [bookingId, setBookingId] = useState(initialBookingId ?? trip?.bookings.find((b) => b.cost)?.id ?? "");
  const [payer, setPayer] = useState(initialPayer ?? "chris");
  const [amount, setAmount] = useState("");
  const [cardGbp, setCardGbp] = useState("");
  const [chrisShare, setChrisShare] = useState("");
  const [paulShare, setPaulShare] = useState("");
  const [customShares, setCustomShares] = useState(false);
  const [date, setDate] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [balanceNotice, setBalanceNotice] = useState<string | null>(null);
  const idempotencyRef = useRef(createId("idem"));
  const primedBooking = useRef<string | null>(null);
  const remainingAtEdit = useRef<number | null>(null);
  const householdIds = trip?.households.map((h) => h.id) ?? ["chris", "paul"];
  const booking = trip?.bookings.find((b) => b.id === bookingId);

  function applyDefaults(nextBookingId: string, creditMinor: number) {
    if (!trip) return;
    const next = trip.bookings.find((b) => b.id === nextBookingId);
    if (!next?.cost || creditMinor <= 0) {
      setChrisShare("");
      setPaulShare("");
      return;
    }
    const funded = defaultFundedAllocations(next, creditMinor, trip.supplierPayments, trip.supplierRefunds, householdIds);
    setChrisShare(((funded.find((row) => row.householdId === "chris")?.amountMinor ?? 0) / 100).toString());
    setPaulShare(((funded.find((row) => row.householdId === "paul")?.amountMinor ?? 0) / 100).toString());
  }

  function primeBooking(nextId: string) {
    if (!trip) return;
    const next = trip.bookings.find((b) => b.id === nextId);
    const leftover = remainingTotal(trip, nextId);
    const credit = leftover > 0 ? leftover : next?.cost?.amountMinor ?? 0;
    setAmount(credit > 0 ? (credit / 100).toString() : "");
    setCustomShares(false);
    setCardGbp("");
    setDate(todayInZone(new Date(), trip.trip.homeTimezone));
    applyDefaults(nextId, credit);
    primedBooking.current = nextId;
    remainingAtEdit.current = leftover;
    setDirty(false);
    setBalanceNotice(null);
  }

  useEffect(() => {
    if (!trip) return;
    if (primedBooking.current !== bookingId) {
      primeBooking(bookingId);
      return;
    }
    if (dirty) {
      const leftover = remainingTotal(trip, bookingId);
      if (remainingAtEdit.current != null && leftover !== remainingAtEdit.current) {
        setBalanceNotice(
          `The remaining supplier balance is now ${booking?.cost ? formatMinor(booking.cost.currency, leftover) : leftover}. Your draft is unchanged. Review it before saving.`,
        );
      }
      return;
    }
    primeBooking(bookingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, trip]);

  if (!trip) return null;
  const amountMinor = booking?.cost && amount ? Math.round(Number(amount) * 100) : 0;
  const funded: Allocation[] = [
    Number(chrisShare) > 0 ? { householdId: "chris", amountMinor: Math.round(Number(chrisShare) * 100) } : null,
    Number(paulShare) > 0 ? { householdId: "paul", amountMinor: Math.round(Number(paulShare) * 100) } : null,
  ].filter((row): row is Allocation => Boolean(row));
  const fundedSum = funded.reduce((sum, row) => sum + row.amountMinor, 0);
  const preview = booking?.cost && amountMinor > 0 && fundedSum === amountMinor
    ? previewPayment(booking, trip.supplierPayments, trip.supplierRefunds, householdIds, {
      payerHouseholdId: payer,
      credit: { currency: booking.cost.currency, amountMinor },
      fundedAllocations: funded,
    })
    : null;
  const labels = Object.fromEntries(trip.households.map((h) => [h.id, h.label]));
  const previewText = preview && booking?.cost
    ? fundingPreviewText({
      payerLabel: labels[payer] ?? payer,
      householdLabels: labels,
      funded,
      claims: preview.claims,
      format: formatMinor,
      currency: booking.cost.currency,
    })
    : null;

  async function savePayment() {
    if (!trip || !booking?.cost || amountMinor <= 0) {
      setError("Enter the amount credited to the booking.");
      return;
    }
    if (funded.length === 0) {
      setError("Enter the household shares this credit covers.");
      return;
    }
    if (fundedSum !== amountMinor) {
      setError("Household shares must add up to the credited amount.");
      return;
    }
    setError(null);
    setPending(true);
    try {
      await postPayment({
        bookingId,
        payerHouseholdId: payer,
        credit: { currency: booking.cost.currency, amountMinor },
        actualCharge: cardGbp ? { currency: "GBP", amountMinor: Math.round(Number(cardGbp) * 100) } : undefined,
        fundedAllocations: funded,
        date: date || todayInZone(new Date(), trip.trip.homeTimezone),
        idempotencyKey: idempotencyRef.current,
      });
      setMessage("Payment saved.");
      idempotencyRef.current = createId("idem");
      setDirty(false);
      primedBooking.current = null;
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the payment.");
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="card money-form" onSubmit={(e) => { e.preventDefault(); if (!pending) void savePayment(); }}>
      <h3 className="section-title">Record payment</h3>
      <p className="muted">This credits the supplier. If one household covers the other, a reimbursement appears separately.</p>
      <label className="field">Which booking?
        <select value={bookingId} onChange={(e) => { setBookingId(e.target.value); primedBooking.current = null; }}>
          {trip.bookings.filter((b) => b.cost).map((b) => (
            <option key={b.id} value={b.id}>{bookingDisplayTitle(b, trip)}</option>
          ))}
        </select>
      </label>
      <label className="field">Who paid?
        <select value={payer} onChange={(e) => { setPayer(e.target.value); setDirty(true); }}>
          {trip.households.map((h) => <option key={h.id} value={h.id}>{h.label}</option>)}
        </select>
      </label>
      <label className="field">How much was credited to the booking? {booking?.cost ? `(${booking.cost.currency})` : ""}
        <input
          value={amount}
          onChange={(e) => {
            setDirty(true);
            setAmount(e.target.value);
            const next = Math.round(Number(e.target.value) * 100);
            if (!customShares && Number.isFinite(next) && next > 0) applyDefaults(bookingId, next);
          }}
          inputMode="decimal"
        />
      </label>
      <label className="field">Payment date
        <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setDirty(true); }} />
      </label>
      <label className="field">Any actual GBP card amount? (optional)
        <input value={cardGbp} onChange={(e) => { setCardGbp(e.target.value); setDirty(true); }} inputMode="decimal" />
      </label>
      <details className="money-fold">
        <summary>Adjust household shares</summary>
        <fieldset className="field">
          <legend>Household shares this credit covers</legend>
          <label className="field">Chris
            <input
              value={chrisShare}
              onChange={(e) => { setCustomShares(true); setChrisShare(e.target.value); setDirty(true); }}
              inputMode="decimal"
            />
          </label>
          <label className="field">Paul
            <input
              value={paulShare}
              onChange={(e) => { setCustomShares(true); setPaulShare(e.target.value); setDirty(true); }}
              inputMode="decimal"
            />
          </label>
        </fieldset>
      </details>
      {previewText ? <p>{previewText}</p> : <p className="muted">Shares follow the remaining allocation on the selected booking.</p>}
      {preview && booking?.cost ? (
        <section className="banner">
          <p>After this save, supplier balance {formatMinor(booking.cost.currency, preview.supplierRemaining)}.</p>
        </section>
      ) : null}
      {balanceNotice ? <p className="banner warn">{balanceNotice}</p> : null}
      {error ? <p className="banner crit">{error}</p> : null}
      <button className="btn" type="submit" disabled={pending}>{pending ? "Saving…" : "Save payment"}</button>
      {message ? <p>{message}</p> : null}
    </form>
  );
}

export function SettlementForm({
  onSaved,
  initialFrom,
  initialTo,
}: {
  onSaved: () => void;
  initialFrom?: string;
  initialTo?: string;
}) {
  const { trip } = useTrip();
  const [fromId, setFromId] = useState(initialFrom ?? "paul");
  const [toId, setToId] = useState(initialTo ?? "chris");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [date, setDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const idempotencyRef = useRef(createId("idem"));
  useEffect(() => {
    if (!date && trip) setDate(todayInZone(new Date(), trip.trip.homeTimezone));
  }, [date, trip]);
  if (!trip) return null;
  const fromLabel = trip.households.find((h) => h.id === fromId)?.label ?? fromId;
  const toLabel = trip.households.find((h) => h.id === toId)?.label ?? toId;

  return (
    <form
      className="card money-form"
      onSubmit={(e) => {
        e.preventDefault();
        const amountMinor = Math.round(Number(amount) * 100);
        if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
          setError("Enter the amount reimbursed.");
          return;
        }
        if (fromId === toId) {
          setError("A household cannot reimburse itself.");
          return;
        }
        setPending(true);
        setError(null);
        void postSettlement({
          fromHouseholdId: fromId,
          toHouseholdId: toId,
          amount: { currency, amountMinor },
          date: date || todayInZone(new Date(), trip.trip.homeTimezone),
          unallocated: false,
          note: `${fromLabel} reimbursed ${toLabel}`,
          idempotencyKey: idempotencyRef.current,
        })
          .then(() => {
            setAmount("");
            idempotencyRef.current = createId("idem");
            onSaved();
          })
          .catch((err: Error) => setError(err.message))
          .finally(() => setPending(false));
      }}
    >
      <h3 className="section-title">Record reimbursement</h3>
      <p className="muted">{fromLabel} paying {toLabel}. This does not change what is still owed to a supplier.</p>
      <label className="field">From
        <select value={fromId} onChange={(e) => setFromId(e.target.value)}>
          {trip.households.map((h) => <option key={h.id} value={h.id}>{h.label}</option>)}
        </select>
      </label>
      <label className="field">To
        <select value={toId} onChange={(e) => setToId(e.target.value)}>
          {trip.households.map((h) => <option key={h.id} value={h.id}>{h.label}</option>)}
        </select>
      </label>
      <label className="field">Amount
        <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
      </label>
      <label className="field">Currency
        <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
          <option value="GBP">GBP</option>
          <option value="THB">THB</option>
        </select>
      </label>
      <label className="field">Date
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      {error ? <p className="banner crit">{error}</p> : null}
      <button className="btn" type="submit" disabled={pending}>{pending ? "Saving…" : "Save reimbursement"}</button>
    </form>
  );
}

export function RefundForm({
  bookingId,
  onSaved,
}: {
  bookingId: string;
  onSaved: () => void;
}) {
  const { trip } = useTrip();
  const [amount, setAmount] = useState("");
  const [paymentId, setPaymentId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const idempotencyRef = useRef(createId("idem"));
  if (!trip) return null;
  const booking = trip.bookings.find((b) => b.id === bookingId);
  const payments = trip.supplierPayments.filter((p) => p.bookingId === bookingId);
  if (!booking?.cost || payments.length === 0) return null;
  const selected = payments.find((p) => p.id === paymentId) ?? payments[0];
  const remaining = selected ? refundableRemainder(selected, trip.supplierRefunds) : 0;

  return (
    <form
      className="card money-form"
      onSubmit={(e) => {
        e.preventDefault();
        const payment = payments.find((p) => p.id === paymentId) ?? payments[0];
        const leftover = refundableRemainder(payment, trip.supplierRefunds);
        if (!amount.trim()) {
          setError("Enter the refund amount. Remaining refundable is shown below.");
          return;
        }
        const amountMinor = Math.round(Number(amount) * 100);
        if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
          setError("Enter the refunded amount.");
          return;
        }
        if (amountMinor > leftover) {
          setError("That is more than remains refundable on this payment.");
          return;
        }
        setPending(true);
        setError(null);
        void postRefund({
          bookingId,
          paymentId: payment.id,
          credit: { currency: booking.cost!.currency, amountMinor },
          recipientHouseholdId: payment.payerHouseholdId,
          reversedAllocations: leftover === amountMinor
            ? payment.fundedAllocations
            : undefined,
          date: todayInZone(new Date(), trip.trip.homeTimezone),
          idempotencyKey: idempotencyRef.current,
        })
          .then(() => {
            idempotencyRef.current = createId("idem");
            onSaved();
          })
          .catch((err: Error) => setError(err.message))
          .finally(() => setPending(false));
      }}
    >
      <h3 className="section-title">Record a supplier refund</h3>
      <p className="muted">Reverses the original payment relationship. Household effects follow the funding on that payment.</p>
      <label className="field">Original payment
        <select value={paymentId || payments[0]?.id} onChange={(e) => { setPaymentId(e.target.value); setAmount(""); }}>
          {payments.map((p) => (
            <option key={p.id} value={p.id}>{p.date} · {formatMinor(p.credit.currency, p.credit.amountMinor)}</option>
          ))}
        </select>
      </label>
      <p>Remaining refundable: {formatMinor(booking.cost.currency, remaining)}</p>
      <label className="field">Amount credited back {`(${booking.cost.currency})`}
        <input value={amount} required onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
      </label>
      {error ? <p className="banner crit">{error}</p> : null}
      <button className="btn-secondary" type="submit" disabled={pending || remaining <= 0}>{pending ? "Saving…" : "Save refund"}</button>
    </form>
  );
}
